// pages/About.js
import React, { useEffect } from 'react';
import './About.css';

const About = () => {
  // Add animation on scroll
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach(el => observer.observe(el));

    return () => {
      animatedElements.forEach(el => observer.unobserve(el));
    };
  }, []);

  const stats = [
    { number: '500+', label: 'Websites Created' },
    { number: '50+', label: 'Team Members' },
    { number: '100%', label: 'Client Satisfaction' },
    { number: '24/7', label: 'Support Available' }
  ];

  const values = [
    {
      icon: '💡',
      title: 'Innovation',
      description: 'Pushing boundaries with cutting-edge technology and creative solutions.'
    },
    {
      icon: '❤️',
      title: 'Customer First',
      description: 'Your success is our priority. We put clients at the heart of everything.'
    },
    {
      icon: '✨',
      title: 'Quality',
      description: 'Pixel-perfect designs and robust code that stands the test of time.'
    },
    {
      icon: '📚',
      title: 'Continuous Growth',
      description: 'Always learning, evolving, and improving to serve you better.'
    }
  ];

  return (
    <div className="about-page">
      {/* Hero Section - Similar to SetupStore hero style */}
      <section className="about-hero">
        <div className="about-container">
          <h1 className="about-hero-title">
            Crafting Digital <span className="highlight">Excellence</span>
          </h1>
          <p className="about-hero-subtitle">
            We're on a mission to empower small businesses with beautiful, affordable e-commerce websites that drive results.
          </p>
          <div className="about-hero-stats">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item animate-on-scroll">
                <span className="stat-number">{stat.number}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section - Two column layout like SetupStore comparison grid */}
      <section className="about-story">
        <div className="about-container">
          <div className="about-story-grid">
            <div className="about-story-card animate-on-scroll">
              <div className="story-card-header">
                <span className="story-badge">Our Journey</span>
                <h2>From Vision to Reality</h2>
              </div>
              <div className="story-card-content">
                <p>
                  Founded in 2024, <strong>Commex</strong> emerged from a simple observation: small businesses in Pakistan needed professional e-commerce websites but found existing solutions either too expensive or too complex.
                </p>
                <p>
                  We set out to change that. Today, we've helped over 500 businesses establish their online presence, combining cutting-edge technology with Pakistani entrepreneurial spirit.
                </p>
                <p>
                  Our team of passionate developers, designers, and digital strategists work tirelessly to create websites that not only look stunning but also drive real business growth.
                </p>
              </div>
            </div>

            <div className="about-mission-card animate-on-scroll">
              <div className="mission-card-header">
                <span className="mission-badge">Our Mission</span>
                <h2>Empowering Small Businesses</h2>
              </div>
              <div className="mission-card-content">
                <p className="mission-text">
                  To democratize e-commerce by providing affordable, high-quality website solutions that help small businesses compete, grow, and thrive in the digital economy.
                </p>
                <div className="mission-quote">
                  <span className="quote-icon">"</span>
                  <p>Every small business deserves a big online presence. We're here to make that happen.</p>
                  <span className="quote-author">— Ahmed Raza, Founder</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section - Grid layout matching products grid */}
      <section className="about-values">
        <div className="about-container">
          <div className="values-header animate-on-scroll">
            <span className="section-badge">What We Believe</span>
            <h2 className="section-title">Our Core Values</h2>
            <p className="values-subtitle">
              These principles guide everything we do, from design decisions to customer interactions.
            </p>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card animate-on-scroll">
                <div className="value-icon">{value.icon}</div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="about-container">
          <div className="cta-content animate-on-scroll">
            <h2>Ready to Start Your Journey?</h2>
            <p>Join hundreds of successful businesses that trust Commex for their online presence.</p>
            <div className="cta-buttons">
              <a href="/contact" className="cta-primary">Get Started Today</a>
              <a href="/demo" className="cta-secondary" target="_blank" rel="noopener noreferrer">View Demo</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;